// Council illustrations reuse the models players see in their village. The first
// open renders a small portrait atlas into one temporary context; later redraws
// only create ordinary images. Shared game geometry is never disposed here.
import {
  AmbientLight, Box3, BufferGeometry, DirectionalLight, DoubleSide, Group, Mesh,
  MeshLambertMaterial, OrthographicCamera, Scene, Vector3, WebGLRenderer,
} from 'three'
import { RESOURCES, type BuildingType, type Resource, type Stockpile } from '../../shared/types'
import type { TownHallPhase } from '../../shared/townhall'
import { buildingGeometry, rackItemGeometry, sitePileGeometry } from '../render3d/models3d/buildings3d'
import { cargoGeometry, unitGeometry } from '../render3d/models3d/units3d'
import { MeshBuilder } from '../render3d/models3d/mesh'
import { MAT } from '../render3d/models3d/palette3d'

export type CouncilArtKind = 'camp' | 'settlement' | 'timberhall' | 'fortifiedhall' | 'keep'
  | 'farm' | 'house' | 'storage' | 'workshop' | 'smithy' | 'watch' | 'people'
  | 'tools' | 'wildlife' | 'balanced' | 'industry'

const KINDS: CouncilArtKind[] = ['camp', 'settlement', 'timberhall', 'fortifiedhall', 'keep',
  'farm', 'house', 'storage', 'workshop', 'smithy', 'watch', 'people', 'tools',
  'wildlife', 'balanced', 'industry']
const PHASES: Partial<Record<CouncilArtKind, TownHallPhase>> = {
  camp: 1, settlement: 2, timberhall: 3, fortifiedhall: 4, keep: 5,
}
const TEAL = '#69a79a'
const portraits = new Map<CouncilArtKind, string>()
let rendered = false

function diorama(kind: CouncilArtKind, material: MeshLambertMaterial, owned: BufferGeometry[]): Group {
  const group = new Group()
  const add = (geometry: BufferGeometry, x = 0, z = 0, scale = 1, yaw = 0): Mesh => {
    const mesh = new Mesh(geometry, material)
    mesh.position.set(x, 0, z); mesh.scale.setScalar(scale); mesh.rotation.y = yaw
    group.add(mesh)
    return mesh
  }
  const building = (type: BuildingType, x = 0, z = 0, scale = 1, variant = 0) =>
    add(buildingGeometry(type, TEAL, 1, variant), x, z, scale)
  const person = (look: string, x: number, z: number, scale = 1, color = TEAL, yaw = -.15) =>
    add(unitGeometry('serf', look, color), x, z, scale, yaw)
  const phase = PHASES[kind]
  if (phase) {
    add(buildingGeometry('townhall', TEAL, 1, phase > 1 ? 3 : 0, phase))
    person('hammer', 23, -35, .75)
  } else switch (kind) {
    case 'farm':
      building('farm', -20, -9, .7)
      person('hoe', 17, -22, 1)
      break
    case 'house':
      building('house'); person('idle', 22, -14, .85, '#b98556')
      break
    case 'storage':
      building('storehouse', 0, 0, 1, 3)
      add(sitePileGeometry('wood'), 22, -15, 1.25)
      break
    case 'workshop':
      building('workbench', -7, 4)
      person('hammer', 14, -12, 1.15)
      add(sitePileGeometry('wood'), -17, -17, .9)
      break
    case 'smithy':
      building('workbench', -8, 6)
      person('hammer', 15, -10, 1.15, '#7f91a4')
      add(cargoGeometry('iron'), -18, -13, 1.2).position.y = -8
      add(rackItemGeometry('sword'), -1, -17, 1.5).position.y = 5
      break
    case 'watch':
      building('palisade', -6, 13, .66)
      person('spearman', -9, -7, 1.1)
      person('spearman', 12, -11, 1.15, '#b58e54', -.35)
      break
    case 'people':
      person('idle', -13, 2, 1, '#b98c59', .15)
      person('hammer', 0, -10, 1.15)
      person('hoe', 15, 3, .95, '#889967', -.45)
      break
    case 'tools':
      building('workbench', 0, 5, .9)
      for (const [item, x, tilt] of [['hammer', -14, -.3], ['axe', 0, .25], ['hoe', 14, .45]] as const) {
        const tool = add(rackItemGeometry(item), x, -13, 2.5)
        tool.rotation.z = tilt; tool.position.y = 10
      }
      break
    case 'wildlife':
      add(unitGeometry('bear', '', ''), -8, 5, 1.1, -.3)
      add(unitGeometry('wolf', '', ''), 15, -9, 1.1, -.3)
      break
    case 'balanced':
      building('house', -16, 6, .75)
      building('farm', 8, -8, .37)
      person('hammer', 9, -23, .9)
      break
    case 'industry':
      building('workbench', 2, 8, .8)
      person('axe', -13, -12, 1.05)
      add(sitePileGeometry('wood'), 15, -13, 1.7)
      add(sitePileGeometry('stone'), 21, 9, 1.2)
      break
  }
  // Small grounded islands make silhouettes legible without opaque image boxes.
  const bounds = new Box3().setFromObject(group)
  const size = bounds.getSize(new Vector3()), center = bounds.getCenter(new Vector3())
  const radius = Math.max(size.x, size.z) * .64
  const ground = new MeshBuilder()
  ground.cyl(center.x, -3.1, center.z, radius, 2.8, 6, '#58614a')
  ground.cyl(center.x, -.3, center.z, radius, .32, 6, '#79916a')
  // A quiet path and a few stones connect each miniature to the game's terrain.
  ground.box(center.x + radius * .12, .15, center.z - radius * .45,
    radius * .35, .25, radius * .3, '#b1a17a', -.3)
  for (const [x, z, s] of [[-.72, -.18, 1], [.56, .57, .7], [-.39, .61, .6]]) {
    ground.gem(center.x + radius * x, radius * .025, center.z + radius * z,
      radius * .1 * s, radius * .08 * s, radius * .09 * s, MAT.stone)
  }
  const geometry = ground.geometry()
  owned.push(geometry)
  add(geometry)
  return group
}

function renderPortraits(): void {
  if (rendered) return
  rendered = true
  let renderer: WebGLRenderer | undefined
  const owned: BufferGeometry[] = []
  const material = new MeshLambertMaterial({ vertexColors: true, flatShading: true, side: DoubleSide })
  try {
    renderer = new WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' })
    renderer.setSize(360, 248)
    renderer.setClearColor(0, 0)
    const scene = new Scene()
    scene.add(new AmbientLight(0xe5edf1, 1.6))
    const sun = new DirectionalLight(0xffe6b8, 2.5)
    sun.position.set(-60, 120, -90)
    scene.add(sun)
    const camera = new OrthographicCamera(-1, 1, 1, -1, .1, 2000)
    const aspect = 360 / 248
    for (const kind of KINDS) {
      const group = diorama(kind, material, owned)
      scene.add(group)
      const bounds = new Box3().setFromObject(group)
      const center = bounds.getCenter(new Vector3())
      const distance = Math.max(30, bounds.getSize(new Vector3()).length()) * 3
      camera.position.copy(center).add(new Vector3(1, 1.05, -1.5).normalize().multiplyScalar(distance))
      camera.lookAt(center); camera.updateMatrixWorld(true)
      // Frame the actual projected bounds, so small tools are as readable as halls.
      let halfHeight = 0
      for (const x of [bounds.min.x, bounds.max.x]) for (const y of [bounds.min.y, bounds.max.y]) {
        for (const z of [bounds.min.z, bounds.max.z]) {
          const point = new Vector3(x, y, z).applyMatrix4(camera.matrixWorldInverse)
          halfHeight = Math.max(halfHeight, Math.abs(point.y), Math.abs(point.x) / aspect)
        }
      }
      halfHeight *= 1.07
      camera.left = -halfHeight * aspect; camera.right = halfHeight * aspect
      camera.top = halfHeight; camera.bottom = -halfHeight
      camera.updateProjectionMatrix()
      renderer.render(scene, camera)
      portraits.set(kind, renderer.domElement.toDataURL('image/png'))
      scene.remove(group)
    }
  } catch {
    // Browsers without an available WebGL context get the local vector portraits.
  } finally {
    for (const geometry of owned) geometry.dispose()
    material.dispose()
    renderer?.dispose()
    renderer?.forceContextLoss()
  }
}

function svg(body: string, viewBox = '0 0 64 64'): SVGSVGElement {
  const holder = document.createElement('span')
  holder.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" fill="none" aria-hidden="true" focusable="false">${body}</svg>`
  return holder.firstElementChild as SVGSVGElement
}

const RESOURCE_DRAWING: Record<Resource, string> = {
  food: '<path d="M14 34h36l-5 21H19Z" fill="#b68c50"/><path d="M13 34h38v6H13Z" fill="#e2bb71"/><path d="M25 35V14m14 21V10" stroke="#eac865" stroke-width="3"/><path d="m25 28-9-7v-6l9 6 9-8v7Zm14-8-8-7V7l8 6 9-7v7Z" fill="#f0d37c"/>',
  wood: '<path d="m12 27 29-14 12 8-29 16Z" fill="#b28552"/><path d="m24 37 29-16v12L24 49Z" fill="#815b38"/><path d="m12 27 12 10v12L12 39Z" fill="#e0b976"/><path d="m8 39 26-13 12 8-26 14Z" fill="#a77747"/><path d="m20 48 26-14v11L20 58Z" fill="#765138"/><path d="M8 39v11l12 8V48Z" fill="#d2a267"/>',
  stone: '<path d="m8 36 10-18 20-5 17 17-5 22-28 5Z" fill="#acb8b5"/><path d="m8 36 21-4 9-19-20 5Z" fill="#d3d9ce"/><path d="m29 32 21 20 5-22-17-17Z" fill="#809394"/><path d="m8 36 14 21 28-5-21-20Z" fill="#97a6a1"/>',
  iron: '<path d="m9 38 10-20 21-6 15 20-8 21-27 3Z" fill="#647987"/><path d="m19 18 12 18 9-24Z" fill="#abc0c4"/><path d="m31 36 16 17 8-21-15-20Z" fill="#8e9fa9"/><path d="m15 38 10-5 7 10-11 5Z" fill="#c2ced0"/>',
  gold: '<path d="m8 39 8-12h19l7 12-7 9H16Z" fill="#e8b944"/><path d="M8 39h34l-7 9H16Z" fill="#b5802e"/><path d="m30 39 7-12h18l6 12-6 9H37Z" fill="#dba638"/><path d="M30 39h31l-6 9H37Z" fill="#b5802e"/><path d="m21 24 7-12h18l7 12-7 9H28Z" fill="#f5d373"/><path d="M21 24h32l-7 9H28Z" fill="#c69737"/>',
  dirt: '<path d="m6 48 12-16 13-14 16 16 12 14-25 10Z" fill="#9e7850"/><path d="m18 32 13-14 4 19L6 48Z" fill="#c69a64"/><path d="m35 37 12-3 12 14-25 10Z" fill="#775a43"/>',
  sand: '<path d="m6 48 13-12 12-18 16 18 12 12-25 10Z" fill="#d2b776"/><path d="m19 36 12-18 4 19L6 48Z" fill="#f0d897"/><path d="m35 37 12-1 12 12-25 10Z" fill="#b39b65"/>',
}

/** Decorative, color-coded vector resource symbol, including on Canvas devices. */
export function resourceIcon(resource: Resource): HTMLElement {
  const icon = document.createElement('span')
  icon.className = 'council-resource-icon'
  icon.setAttribute('aria-hidden', 'true')
  icon.append(svg(RESOURCE_DRAWING[resource]))
  return icon
}

function fallback(kind: CouncilArtKind): SVGSVGElement {
  const base = '<path d="m15 83 65-32 65 32v12l-65 32-65-32Z" fill="#52604a"/><path d="m15 83 65-32 65 32-65 32Z" fill="#84966a"/>'
  let subject: string
  if (kind === 'camp') {
    subject = '<path d="m37 85 33-53 39 53Z" fill="#e2c58d"/><path d="m70 32 48 14 21 45-30-6Z" fill="#ac9a70"/><path d="m58 85 12-26 14 26Z" fill="#66513a"/><path d="m25 89 12-15 8 14-7 8Z" fill="#efaa56"/>'
  } else if (kind === 'people' || kind === 'watch') {
    subject = '<g fill="#d2a177"><circle cx="58" cy="46" r="10"/><circle cx="94" cy="53" r="9"/></g><path d="M43 60h30v34H43Z" fill="#79ae9c"/><path d="M81 66h27v31H81Z" fill="#c8a468"/><path d="M48 94v16m19-16v16m20-13v15m16-15v15" stroke="#614d3c" stroke-width="7"/>'
    if (kind === 'watch') subject += '<path d="m123 101-5-74" stroke="#c8ac75" stroke-width="4"/><path d="m118 15-5 16h11Z" fill="#cbd3c7"/>'
  } else if (kind === 'wildlife') {
    subject = '<path d="m34 62 34-14 37 15 15 18-7 12-16-5-35 4-25-14Z" fill="#9e7953"/><path d="M42 77v24m24-13v19m27-25v19m16-18v17" stroke="#69513d" stroke-width="8"/><path d="m101 69 5-17 13 21Z" fill="#75573e"/>'
  } else if (kind === 'tools' || kind === 'workshop' || kind === 'smithy' || kind === 'industry') {
    subject = '<path d="M41 92 87 39m-2 63-23-58" stroke="#d2ac76" stroke-width="8"/><path d="m73 30 16-7 26 22-16 12Z" fill="#c6cabb"/><path d="m45 34 15-7 24 12-6 14Z" fill="#8b9f9f"/>'
  } else if (kind === 'farm') {
    subject = '<path d="m33 89 30-19 58 21-31 20Z" fill="#786348"/><path d="M51 87V55m25 40V45m25 45V61" stroke="#e7c76d" stroke-width="4"/><path d="m51 76-12-9v-9l12 9 11-11v9Zm25-10-12-9v-9l12 9 11-11v9Zm25 14-11-8v-8l11 8 10-10v8Z" fill="#e6c56b"/>'
  } else {
    const keep = kind === 'keep' || kind === 'fortifiedhall'
    subject = `<path d="m38 58 37-20 43 23v37l-42 18-38-19Z" fill="${keep ? '#b6bcb0' : '#d7c394'}"/><path d="m76 79 42-18v37l-42 18Z" fill="${keep ? '#84968d' : '#9d8d63'}"/><path d="M52 101V79l17-3v31Z" fill="#635740"/>`
    if (keep) subject += '<path d="M34 59V37h11v9h12V29h12v17h13V31h12v15h13V38h15v26L77 84Z" fill="#c7cabb"/>'
    else subject += '<path d="m27 61 42-39 61 32-45 31Z" fill="#cea862"/><path d="m69 22 61 32-12 7-42-18Z" fill="#e6c17c"/>'
  }
  return svg(base + subject, '0 0 160 138')
}

/** A decorative portrait. Callers provide the visible title and action semantics. */
export function councilArt(kind: CouncilArtKind, className = ''): HTMLElement {
  renderPortraits()
  const wrapper = document.createElement('span')
  wrapper.className = `council-art council-art-${kind}${className ? ` ${className}` : ''}`
  wrapper.setAttribute('aria-hidden', 'true')
  const source = portraits.get(kind)
  if (source) {
    const img = document.createElement('img')
    img.src = source; img.alt = ''; img.width = 360; img.height = 248
    img.draggable = false
    wrapper.append(img)
  } else wrapper.append(fallback(kind))
  return wrapper
}

/** Costs or goods with resource names available to screen readers and on hover. */
export function resourceChips(goods: Partial<Stockpile>, stockpile?: Partial<Stockpile>): HTMLElement {
  const chips = document.createElement('span')
  chips.className = 'council-resources'
  for (const resource of RESOURCES) {
    const amount = goods[resource] ?? 0
    if (amount <= 0) continue
    const have = stockpile?.[resource] ?? 0
    const short = stockpile !== undefined && have < amount
    const value = Number.isInteger(amount) ? String(amount) : amount.toFixed(1)
    const chip = document.createElement('span')
    chip.className = `council-resource${short ? ' short' : ''}`
    chip.title = `${value} ${resource}${short ? ` · ${Math.floor(have)} available` : ''}`
    chip.setAttribute('aria-label', chip.title)
    const label = document.createElement('strong')
    label.className = 'council-resource-value'
    label.textContent = value
    const name = document.createElement('span')
    name.className = 'council-resource-name'
    name.textContent = resource[0].toUpperCase() + resource.slice(1)
    chip.append(resourceIcon(resource), label, name)
    chips.append(chip)
  }
  return chips
}
